import { OrbitControls } from '@react-three/drei'
import { Canvas, useThree } from '@react-three/fiber'
import { useEffect, useMemo, useState } from 'react'
import { ancoraPorId, type PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'
import { postoDoVeiculo, type ConfiguracaoVeiculo } from '../dominio/didatico/configuracaoVeiculo'
import { ProvedorMaquete, type ValorMaquete } from './componentes/contextoMaquete'
import { CenaCinematica, type PropsCenaCinematica } from './cinematica/CenaCinematica'
import { CenaExtracao, type PropsCenaExtracao } from './extracao/CenaExtracao'
import { CenaMaquete, type PropsCenaMaquete } from './veiculo/CenaMaquete'
import { CenaEstabilizacao, type PropsCenaEstabilizacao } from './estabilizacao/CenaEstabilizacao'
import { CenaTabletop, type PropsCenaTabletop } from './tabletop/CenaTabletop'
import { CenaManejoVidros, type PropsCenaManejoVidros } from './vidros/CenaManejoVidros'
import { LagrimaProtecao3D } from './vidros/LagrimaProtecao3D'
import { calcularPoseLagrima, vitimaEstaProxima } from './oficina/posicionamentoLagrima'
import { PalcoEstudio, CONFIGURACAO_GL_ESTUDIO } from './ambiente/PalcoEstudio'
import { ContadorFps } from './ambiente/ContadorFps'
import { MonitorQualidade } from './ambiente/MonitorQualidade'
import { ProvedorPosProcessamento } from './ambiente/registroPosProcessamento'
import { PosProcessamento } from './ambiente/PosProcessamento'
import { useQualidadeAdaptativa } from './ambiente/useQualidadeAdaptativa'
import { RECURSOS_QUALIDADE } from './ambiente/qualidadeGrafica'
import { ProvedorQualidade } from './ambiente/contextoQualidade'
import { DestaqueSelecaoAnatomia } from './anatomia/DestaqueSelecaoAnatomia'
import { DebugHitVeicular3D } from './inspecao/DebugHitVeicular3D'
import type { HitVeiculo } from '../dominio/didatico/inspecaoVeicular'
import { ControleCamera3D } from './camera/ControleCamera3D'
import {
  DISTANCIA_MAXIMA,
  DISTANCIA_MINIMA,
  alvoCamera,
  type ComandoCamera,
} from './camera/comandosCamera'
import { enquadramentoInteriorExtracao } from './extracao/enquadramentoCamera'
import { FerramentaPosicionavel3D } from './oficina/FerramentaPosicionavel3D'
import { TransicaoSuave } from './componentes/TransicaoSuave'
import { HaloZonaCorte3D } from './oficina/HaloZonaCorte3D'
import {
  dadosDaPorta,
  posicaoFerramentaNaPorta,
  zonaOperacaoPorId,
  type AjusteFerramentaPorta,
  type IdPortaOficina,
} from './oficina/zonasOperacaoPorta'
import { posicaoNoQuadro, quadroDaPorta, ZONAS_DA_CARROCERIA } from './oficina/quadroDaPorta'
import { posicaoNoQuadroDaTampa, quadroDaTampa } from './oficina/quadroDaTampa'
import { posicaoFerramentaNaTampa } from './oficina/zonasOperacaoTampa'
import { posicaoNoQuadroDaTerceiraPorta, quadroDaTerceiraPorta } from './oficina/quadroDaTerceiraPorta'
import {
  posicaoFerramentaNaTerceiraPorta,
  type IdLadoTerceiraPorta,
} from './oficina/zonasOperacaoTerceiraPorta'
import { ZonasOperacaoPorta3D } from './oficina/ZonasOperacaoPorta3D'
import { ZonasOperacaoTampa3D } from './oficina/ZonasOperacaoTampa3D'
import { ZonasOperacaoTerceiraPorta3D } from './oficina/ZonasOperacaoTerceiraPorta3D'
import { enquadramentoOficina, enquadramentoZonaOficina, type AlvoOficina } from './oficina/enquadramentoOficina'
import type {
  ConfiguracaoOficinaCorte,
  EstadoPortaOficina,
  FerramentaOficinaCorte,
  IdZonaOperacaoPorta,
} from '../dominio/didatico/oficinaCorte'
import type { ModoAlargador } from '../dominio/didatico/interacaoOficina'
import { ehZonaTetoLateral, type IdZonaTetoLateral } from '../dominio/didatico/rebatimentoLateralTeto'
import { enquadramentoTetoLateral, zonaTetoLateralPorId } from './oficina/zonasTetoLateral'
import { FerramentaHidraulica3D } from './oficina/FerramentaHidraulica3D'
import { CorteParabrisaTeto3D } from './oficina/CorteParabrisaTeto3D'
import { ehZonaElevacao, type IdZonaElevacaoPainel } from '../dominio/didatico/elevacaoPainel'
import { pontosElevacaoPainel, zonaElevacaoPorId } from './oficina/zonasElevacaoPainel'
import { ElevacaoPainel3D } from './oficina/ElevacaoPainel3D'
import { ehZonaRebatimento, type IdZonaRebatimentoPainel } from '../dominio/didatico/rebatimentoPainel'
import { pontosRebatimentoPainel, zonaRebatimentoPorId } from './oficina/zonasRebatimentoPainel'
import { RebatimentoPainel3D } from './oficina/RebatimentoPainel3D'
import type { EstadoExtensorOficina, IdApoioExtensor } from '../dominio/didatico/oficinaExtensor'
import type { EstadoTampaOficina, IdZonaOperacaoTampa } from '../dominio/didatico/oficinaPortaMalas'
import type {
  EstadoTerceiraPortaOficina,
  IdZonaOperacaoTerceiraPorta,
} from '../dominio/didatico/oficinaTerceiraPorta'

interface Props {
  contexto: ValorMaquete
  maquete: PropsCenaMaquete
  cinematica: PropsCenaCinematica
  estabilizacao: PropsCenaEstabilizacao
  tabletop: PropsCenaTabletop
  extracao: PropsCenaExtracao
  vidros?: PropsCenaManejoVidros
  oficinaCorte?: {
    ativa: boolean
    configuracao: ConfiguracaoOficinaCorte
    ferramenta: FerramentaOficinaCorte
    modoAlargador: ModoAlargador
    progresso: number
    progressoEstrutural: Record<string, number>
    idPorta: IdPortaOficina
    zonaSelecionada: IdZonaOperacaoPorta | IdZonaRebatimentoPainel | IdZonaElevacaoPainel | IdZonaTetoLateral | null
    /** Estado da porta, para os marcadores mostrarem o que já foi feito. */
    estado: EstadoPortaOficina
    ajusteFerramenta: AjusteFerramentaPorta
    afastamento?: number
    ferramentaVisivel?: boolean
    /** Player: 0 = garras no estado inicial (no ar), 1 = pose de trabalho. */
    preparo?: number
    estadoInicial?: 'aberto' | 'fechado' | null
    /** Player: a ferramenta desliza entre zonas em vez de saltar. */
    deslizar?: boolean
    desabilitarArraste?: boolean
    aoSelecionarZona: (id: IdZonaOperacaoPorta) => void
    aoMoverFerramenta: (deslocamentoY: number, deslocamentoZ: number) => void
    /** Pressionar o marcador 3D já começa a fechar a ferramenta, como segurar o botão do painel. */
    aoPressionarFerramenta: (id: IdZonaOperacaoPorta) => void
    aoSoltarFerramenta: () => void
    aoPrevisualizarZona: (id: IdZonaOperacaoPorta) => void
    extensor: {
      estado: EstadoExtensorOficina
      progresso: number
      aoEscolherApoio: (id: IdApoioExtensor) => void
      aoPrevisualizarApoio: (id: IdApoioExtensor) => void
    }
  }
  /** Mesma oficina, alvo na tampa traseira em vez de uma porta lateral. */
  oficinaTampa?: {
    ativa: boolean
    configuracao: ConfiguracaoOficinaCorte
    ferramenta: FerramentaOficinaCorte
    progresso: number
    progressoEstrutural: Record<string, number>
    zonaSelecionada: IdZonaOperacaoTampa | null
    estado: EstadoTampaOficina
    ajusteFerramenta: AjusteFerramentaPorta
    afastamento?: number
    ferramentaVisivel?: boolean
    /** Player: 0 = garras no estado inicial (no ar), 1 = pose de trabalho. */
    preparo?: number
    estadoInicial?: 'aberto' | 'fechado' | null
    /** Player: a ferramenta desliza entre zonas em vez de saltar. */
    deslizar?: boolean
    desabilitarArraste?: boolean
    aoSelecionarZona: (id: IdZonaOperacaoTampa) => void
    aoMoverFerramenta: (deslocamentoY: number, deslocamentoZ: number) => void
    aoPressionarFerramenta: (id: IdZonaOperacaoTampa) => void
    aoSoltarFerramenta: () => void
    aoPrevisualizarZona: (id: IdZonaOperacaoTampa) => void
  }
  /** Mesma oficina, alvo na terceira porta (footprint da porta traseira sem dobradiça/trava/vidro reais). */
  oficinaTerceiraPorta?: {
    ativa: boolean
    configuracao: ConfiguracaoOficinaCorte
    ferramenta: FerramentaOficinaCorte
    progresso: number
    progressoEstrutural: Record<string, number>
    lado: IdLadoTerceiraPorta
    zonaSelecionada: IdZonaOperacaoTerceiraPorta | null
    estado: EstadoTerceiraPortaOficina
    ajusteFerramenta: AjusteFerramentaPorta
    afastamento?: number
    ferramentaVisivel?: boolean
    /** Player: 0 = garras no estado inicial (no ar), 1 = pose de trabalho. */
    preparo?: number
    estadoInicial?: 'aberto' | 'fechado' | null
    /** Player: a ferramenta desliza entre zonas em vez de saltar. */
    deslizar?: boolean
    desabilitarArraste?: boolean
    aoSelecionarZona: (id: IdZonaOperacaoTerceiraPorta) => void
    aoMoverFerramenta: (deslocamentoY: number, deslocamentoZ: number) => void
    aoPressionarFerramenta: (id: IdZonaOperacaoTerceiraPorta) => void
    aoSoltarFerramenta: () => void
    aoPrevisualizarZona: (id: IdZonaOperacaoTerceiraPorta) => void
  }
  aoInteragirCameraOficina?: () => void
  demonstracaoOficina?: { executando: boolean; recuando: boolean; pausado: boolean; concluida: boolean }
  /** Comando de câmera vindo da trilha de ferramentas. */
  comandoCamera: ComandoCamera
}

function CameraDaExtracao({
  ativa,
  configuracao,
  perfil,
}: {
  ativa: boolean
  configuracao: ConfiguracaoVeiculo
  perfil: PerfilVeiculo
}) {
  const camera = useThree((estado) => estado.camera)
  const controles = useThree((estado) => estado.controls) as {
    target?: { set: (x: number, y: number, z: number) => void }
    update?: () => void
  } | null

  useEffect(() => {
    if (!ativa || !controles) return
    // mesma pose do comando "Interior" da trilha de câmera — ver
    // enquadramentoInteriorExtracao para o porquê dos valores por configuração.
    const { posicao, alvo } = enquadramentoInteriorExtracao(perfil, configuracao)

    camera.position.set(...posicao)
    controles.target?.set(...alvo)
    camera.lookAt(...alvo)
    controles.update?.()
  }, [ativa, configuracao, perfil, camera, controles])

  return null
}

export function Visualizador3D({
  contexto,
  maquete,
  cinematica,
  estabilizacao,
  tabletop,
  extracao,
  vidros,
  oficinaCorte,
  oficinaTampa,
  oficinaTerceiraPorta,
  aoInteragirCameraOficina,
  demonstracaoOficina,
  comandoCamera,
}: Props) {
  const { perfil, modo } = contexto
  const qualidade = useQualidadeAdaptativa()
  // Referência estável: `<OrbitControls target={...}>` reaplica esta prop a
  // cada render (R3F não faz diff por valor em vetores), e um array novo a
  // cada chamada fazia isso reescrever `controls.target` sobre qualquer
  // enquadramento que `ControleCamera3D` tivesse acabado de aplicar — quase
  // imperceptível para um alvo perto do centro do veículo (uma porta), mas
  // puxava a câmera de volta ao centro sempre que algo re-renderizava a cena
  // com um alvo longe dele (a tampa traseira).
  const alvoPadraoCamera = useMemo(() => alvoCamera(perfil), [perfil])
  const [tabletopManipulando, setTabletopManipulando] = useState(false)
  const [hitLocal, setHitLocal] = useState<HitVeiculo | null>(null)
  const contextoComHit = useMemo<ValorMaquete>(() => ({
    ...contexto,
    ultimoHit: contexto.ultimoHit !== undefined ? contexto.ultimoHit : hitLocal,
    aoRegistrarHit: (hit) => {
      setHitLocal(hit)
      contexto.aoRegistrarHit?.(hit)
    },
  }), [contexto, hitLocal])

  useEffect(() => {
    if (modo !== 'tabletop' || tabletop.idSelecionado === null) {
      setTabletopManipulando(false)
    }
  }, [modo, tabletop.idSelecionado])
  const ladoPortaOficina = oficinaCorte?.ativa
    ? dadosDaPorta(perfil, oficinaCorte.idPorta).lado
    : 'esquerdo'
  // a ferramenta encosta na porta onde ela está agora, não onde estaria fechada
  const quadroPortaOficina = oficinaCorte?.ativa
    ? quadroDaPorta(perfil, oficinaCorte.idPorta, oficinaCorte.estado)
    : null
  const posicaoCanonicaFerramenta = oficinaCorte?.ativa
    ? ehZonaTetoLateral(oficinaCorte.zonaSelecionada)
      ? zonaTetoLateralPorId(perfil, oficinaCorte.zonaSelecionada, oficinaCorte.ferramenta === 'serraSabre' ? oficinaCorte.progresso : 0, oficinaCorte.extensor.estado.tetoLateral?.progressoRebatimento).posicao
      : ehZonaElevacao(oficinaCorte.zonaSelecionada)
      ? zonaElevacaoPorId(perfil, oficinaCorte.idPorta, oficinaCorte.zonaSelecionada, oficinaCorte.extensor.estado.elevacao?.alturaPainel, oficinaCorte.extensor.estado.elevacao?.progressoDobra).posicao
      : ehZonaRebatimento(oficinaCorte.zonaSelecionada)
      ? zonaRebatimentoPorId(perfil, oficinaCorte.idPorta, oficinaCorte.zonaSelecionada, oficinaCorte.extensor.estado.deslocamentoPainel).posicao
      : posicaoFerramentaNaPorta(
      perfil,
      oficinaCorte.idPorta,
      oficinaCorte.zonaSelecionada,
      oficinaCorte.ajusteFerramenta,
    )
    : null
  const zonaNaCarroceria = oficinaCorte?.zonaSelecionada != null
    && (ehZonaRebatimento(oficinaCorte.zonaSelecionada) || ehZonaElevacao(oficinaCorte.zonaSelecionada) || ehZonaTetoLateral(oficinaCorte.zonaSelecionada) || ZONAS_DA_CARROCERIA.includes(oficinaCorte.zonaSelecionada))
  const posicaoFerramenta = posicaoCanonicaFerramenta && quadroPortaOficina && !zonaNaCarroceria
    ? posicaoNoQuadro(quadroPortaOficina, posicaoCanonicaFerramenta)
    : posicaoCanonicaFerramenta
  const quadroTampaOficina = oficinaTampa?.ativa ? quadroDaTampa(perfil, oficinaTampa.estado) : null
  const posicaoCanonicaFerramentaTampa = oficinaTampa?.ativa
    ? posicaoFerramentaNaTampa(perfil, oficinaTampa.zonaSelecionada, oficinaTampa.ajusteFerramenta)
    : null
  const posicaoFerramentaTampa = posicaoCanonicaFerramentaTampa && quadroTampaOficina
    ? posicaoNoQuadroDaTampa(quadroTampaOficina, posicaoCanonicaFerramentaTampa)
    : posicaoCanonicaFerramentaTampa
  const quadroTerceiraPortaOficina = oficinaTerceiraPorta?.ativa
    ? quadroDaTerceiraPorta(perfil, oficinaTerceiraPorta.lado, oficinaTerceiraPorta.estado)
    : null
  const posicaoCanonicaFerramentaTerceiraPorta = oficinaTerceiraPorta?.ativa
    ? posicaoFerramentaNaTerceiraPorta(
      perfil,
      oficinaTerceiraPorta.lado,
      oficinaTerceiraPorta.zonaSelecionada,
      oficinaTerceiraPorta.ajusteFerramenta,
    )
    : null
  const posicaoFerramentaTerceiraPorta = posicaoCanonicaFerramentaTerceiraPorta && quadroTerceiraPortaOficina
    ? posicaoNoQuadroDaTerceiraPorta(quadroTerceiraPortaOficina, posicaoCanonicaFerramentaTerceiraPorta)
    : posicaoCanonicaFerramentaTerceiraPorta
  const configuracaoOficina = oficinaCorte?.configuracao ?? oficinaTampa?.configuracao ?? oficinaTerceiraPorta?.configuracao
  const oficinaAtiva = Boolean(oficinaCorte?.ativa || oficinaTampa?.ativa || oficinaTerceiraPorta?.ativa)
  const configuracaoVeiculoOficina: ConfiguracaoVeiculo = configuracaoOficina === 'tombado'
    ? 'lateral'
    : configuracaoOficina === 'capotado' ? 'invertido' : 'sobreRodas'
  const postoOficina = postoDoVeiculo(perfil, configuracaoVeiculoOficina)
  const alvoDaOficina: AlvoOficina | null = oficinaCorte?.ativa
    ? { peca: 'porta', idPorta: oficinaCorte.idPorta }
    : oficinaTampa?.ativa
      ? { peca: 'tampaTraseira' }
      : oficinaTerceiraPorta?.ativa
        ? { peca: 'terceiraPorta', lado: oficinaTerceiraPorta.lado }
        : null
  const zonaAtivaDaOficina = posicaoFerramenta ?? posicaoFerramentaTampa ?? posicaoFerramentaTerceiraPorta
  const pontosCameraRebatimento = oficinaCorte?.ativa && oficinaCorte.extensor.estado.rebatimento
    && (oficinaCorte.extensor.estado.rebatimento.apoioInstalado || oficinaCorte.zonaSelecionada === 'baseExtensor')
      ? pontosRebatimentoPainel(perfil, oficinaCorte.idPorta, oficinaCorte.extensor.estado.deslocamentoPainel) : null
  const pontosCameraElevacao = oficinaCorte?.ativa && oficinaCorte.extensor.estado.elevacao && ehZonaElevacao(oficinaCorte.zonaSelecionada)
    ? pontosElevacaoPainel(perfil, oficinaCorte.idPorta, oficinaCorte.extensor.estado.elevacao.alturaPainel) : null
  const centroCameraOficina: [number, number, number] | null = pontosCameraRebatimento
    ? pontosCameraRebatimento.baseExtensor.map((n, i) => (n + pontosCameraRebatimento.cabecaExtensor[i]) / 2) as [number, number, number]
    : pontosCameraElevacao
      ? [pontosCameraElevacao.apoioSuperior[0] + pontosCameraElevacao.sinal * 0.28, perfil.cinturaY - 0.08 + (oficinaCorte?.extensor.estado.elevacao?.alturaPainel ?? 0) / 2, pontosCameraElevacao.apoioSuperior[2]]
      : zonaAtivaDaOficina
  const poseBaseOficina = alvoDaOficina
    ? centroCameraOficina
      ? enquadramentoZonaOficina(perfil, alvoDaOficina, postoOficina, centroCameraOficina)
      : enquadramentoOficina(perfil, alvoDaOficina, postoOficina)
    : null
  const poseDaOficina = oficinaCorte?.ativa && oficinaCorte.extensor.estado.tetoLateral
    ? enquadramentoTetoLateral(perfil, oficinaCorte.zonaSelecionada === 'tetoRebater' || oficinaCorte.extensor.estado.tetoLateral.rebatido ? undefined : posicaoFerramenta ?? undefined)
    : poseBaseOficina && (pontosCameraRebatimento || pontosCameraElevacao)
    ? { ...poseBaseOficina, posicao: poseBaseOficina.posicao.map((n, i) => poseBaseOficina.alvo[i] + (n - poseBaseOficina.alvo[i]) * (pontosCameraElevacao ? 1.95 : 1.7)) as [number, number, number] }
    : poseBaseOficina
  const poseLagrimaOficina = useMemo(() => {
    if (!maquete.mostrarVitima || !zonaAtivaDaOficina) return null
    const ferramentaAtiva =
      (oficinaCorte?.ativa && oficinaCorte.ferramentaVisivel) ||
      (oficinaTampa?.ativa && oficinaTampa.ferramentaVisivel) ||
      (oficinaTerceiraPorta?.ativa && oficinaTerceiraPorta.ferramentaVisivel) ||
      (demonstracaoOficina && !demonstracaoOficina.concluida && (demonstracaoOficina.executando || demonstracaoOficina.recuando))
    if (!ferramentaAtiva) return null

    try {
      const ancora = ancoraPorId(perfil, maquete.idAncora ?? 'motorista')
      const posVitima: [number, number, number] = [
        ancora.posicao.x,
        ancora.posicao.y + 0.38,
        ancora.posicao.z,
      ]
      if (!vitimaEstaProxima(posVitima, zonaAtivaDaOficina)) return null
      const lado = zonaAtivaDaOficina[0] > 0 ? 'direito' : 'esquerdo'
      return calcularPoseLagrima({
        posicaoVitima: posVitima,
        posicaoPerigo: zonaAtivaDaOficina,
        lado,
      })
    } catch {
      return null
    }
  }, [
    maquete.mostrarVitima,
    maquete.idAncora,
    perfil,
    zonaAtivaDaOficina,
    oficinaCorte?.ativa,
    oficinaCorte?.ferramentaVisivel,
    oficinaTampa?.ativa,
    oficinaTampa?.ferramentaVisivel,
    oficinaTerceiraPorta?.ativa,
    oficinaTerceiraPorta?.ferramentaVisivel,
    demonstracaoOficina,
  ])

  const conteudoMaquete = (
    <>
      <CenaMaquete {...maquete} />
      {oficinaCorte?.ativa && oficinaCorte.extensor.estado.rebatimento && <RebatimentoPainel3D
        perfil={perfil} idPorta={oficinaCorte.idPorta} estado={oficinaCorte.extensor.estado}
        ferramentaVisivel={oficinaCorte.ferramenta === 'extensor' && Boolean(oficinaCorte.ferramentaVisivel)}
        afastamento={oficinaCorte.afastamento} />}
      {oficinaCorte?.ativa && oficinaCorte.extensor.estado.elevacao && <ElevacaoPainel3D
        perfil={perfil} idPorta={oficinaCorte.idPorta} estado={oficinaCorte.extensor.estado.elevacao}
        mostrarAlargador={oficinaCorte.zonaSelecionada === 'apoioAlargadorPainel' && Boolean(oficinaCorte.ferramentaVisivel)}
        mostrarDobra={oficinaCorte.zonaSelecionada === 'abaElevacao' && Boolean(oficinaCorte.ferramentaVisivel)}
        progressoFerramenta={oficinaCorte.progresso}
        afastamento={oficinaCorte.afastamento}
        preparo={oficinaCorte.preparo} estadoInicial={oficinaCorte.estadoInicial} deslizar={oficinaCorte.deslizar} />}
      {oficinaCorte?.ativa && oficinaCorte.extensor.estado.tetoLateral && <CorteParabrisaTeto3D
        perfil={perfil} estado={oficinaCorte.extensor.estado.tetoLateral}
        ferramenta={oficinaCorte.ferramenta === 'machadinha' || oficinaCorte.ferramenta === 'serraSabre' ? oficinaCorte.ferramenta : null}
        progresso={oficinaCorte.progresso} visivel={Boolean(oficinaCorte.ferramentaVisivel)} afastamento={oficinaCorte.afastamento} />}
      {poseLagrimaOficina?.visivel && (
        <LagrimaProtecao3D
          visivel
          posicao={poseLagrimaOficina.posicao}
          rotacao={poseLagrimaOficina.rotacao}
          escala={poseLagrimaOficina.escala}
        />
      )}
      {/* Os marcadores de ponto são da prática livre. No modo guiado o halo
          já aponta o alvo da etapa, e a fileira de miras por cima da porta só
          poluía a cena. */}
      {oficinaCorte?.ativa && !demonstracaoOficina && (
        <ZonasOperacaoPorta3D
          perfil={perfil}
          idPorta={oficinaCorte.idPorta}
          selecionada={ehZonaRebatimento(oficinaCorte.zonaSelecionada) || ehZonaElevacao(oficinaCorte.zonaSelecionada) || ehZonaTetoLateral(oficinaCorte.zonaSelecionada) ? null : oficinaCorte.zonaSelecionada}
          estadoPorta={oficinaCorte.estado}
          ferramenta={oficinaCorte.ferramenta}
          modoAlargador={oficinaCorte.modoAlargador}
          progressoEstrutural={oficinaCorte.progressoEstrutural}
          aoSelecionar={oficinaCorte.aoSelecionarZona}
          aoPressionarFerramenta={oficinaCorte.aoPressionarFerramenta}
          aoSoltarFerramenta={oficinaCorte.aoSoltarFerramenta}
          aoPrevisualizar={oficinaCorte.aoPrevisualizarZona}
        />
      )}
      {oficinaTampa?.ativa && !demonstracaoOficina && (
        <ZonasOperacaoTampa3D
          perfil={perfil}
          selecionada={oficinaTampa.zonaSelecionada}
          estadoTampa={oficinaTampa.estado}
          ferramenta={oficinaTampa.ferramenta}
          progressoEstrutural={oficinaTampa.progressoEstrutural}
          aoSelecionar={oficinaTampa.aoSelecionarZona}
          aoPressionarFerramenta={oficinaTampa.aoPressionarFerramenta}
          aoSoltarFerramenta={oficinaTampa.aoSoltarFerramenta}
          aoPrevisualizar={oficinaTampa.aoPrevisualizarZona}
        />
      )}
      {oficinaTerceiraPorta?.ativa && !demonstracaoOficina && (
        <ZonasOperacaoTerceiraPorta3D
          perfil={perfil}
          lado={oficinaTerceiraPorta.lado}
          selecionada={oficinaTerceiraPorta.zonaSelecionada}
          estadoTerceiraPorta={oficinaTerceiraPorta.estado}
          ferramenta={oficinaTerceiraPorta.ferramenta}
          progressoEstrutural={oficinaTerceiraPorta.progressoEstrutural}
          aoSelecionar={oficinaTerceiraPorta.aoSelecionarZona}
          aoPressionarFerramenta={oficinaTerceiraPorta.aoPressionarFerramenta}
          aoSoltarFerramenta={oficinaTerceiraPorta.aoSoltarFerramenta}
          aoPrevisualizar={oficinaTerceiraPorta.aoPrevisualizarZona}
        />
      )}
      {oficinaCorte?.ativa && oficinaCorte.ferramentaVisivel && !ehZonaTetoLateral(oficinaCorte.zonaSelecionada) && oficinaCorte.zonaSelecionada !== 'apoioAlargadorPainel' && oficinaCorte.zonaSelecionada !== 'abaElevacao' && (
        oficinaCorte.ferramenta === 'alargador'
        || oficinaCorte.ferramenta === 'cortadorHidraulico'
        || oficinaCorte.ferramenta === 'hooligan'
        || oficinaCorte.ferramenta === 'puncaoVidro'
      ) && (
        posicaoFerramenta && <FerramentaPosicionavel3D
          ferramenta={oficinaCorte.ferramenta}
          modoAlargador={oficinaCorte.modoAlargador}
          progresso={oficinaCorte.progresso}
          posicao={posicaoFerramenta}
          anguloGraus={oficinaCorte.ajusteFerramenta.anguloGraus}
          lado={ladoPortaOficina}
          giroPeca={zonaNaCarroceria ? 0 : quadroPortaOficina?.giroY ?? 0}
          afastamento={oficinaCorte.afastamento}
          preparo={oficinaCorte.preparo}
          estadoInicial={oficinaCorte.estadoInicial}
          chaveTransicao={oficinaCorte.zonaSelecionada}
          deslizar={oficinaCorte.deslizar}
          desabilitarArraste={oficinaCorte.desabilitarArraste}
          orientacaoParalela={oficinaCorte.zonaSelecionada === 'paralama'}
          comprimirMacaneta={oficinaCorte.zonaSelecionada === 'macaneta' && oficinaCorte.modoAlargador === 'fechar'}
          alturaEsquadria={oficinaCorte.zonaSelecionada === 'esquadria' && oficinaCorte.modoAlargador === 'abrir'
            ? zonaOperacaoPorId(perfil, oficinaCorte.idPorta, 'esquadria')?.alturaEsquadria : undefined}
          aoMover={() => {}}
        />
      )}
      {oficinaCorte?.ativa && oficinaCorte.ferramentaVisivel && oficinaCorte.ferramenta === 'cortadorHidraulico' && ehZonaTetoLateral(oficinaCorte.zonaSelecionada) && posicaoFerramenta && (
        <group position={posicaoFerramenta} rotation={[0, 0, -Math.PI / 2]} scale={0.72} name="cortador-teto-lateral">
          <group position={[-0.62 - (oficinaCorte.afastamento ?? 0), 0, 0]}
            rotation={[oficinaCorte.zonaSelecionada === 'tetoAlivioA' || oficinaCorte.zonaSelecionada === 'tetoAlivioD' ? Math.PI / 2 : 0, 0, 0]}>
            <TransicaoSuave chave={oficinaCorte.zonaSelecionada} ativa={oficinaCorte.deslizar}>
              <FerramentaHidraulica3D ferramenta="cortadorHidraulico" progresso={oficinaCorte.progresso} ativa={oficinaCorte.progresso > 0}
                preparo={oficinaCorte.preparo} estadoInicial={oficinaCorte.estadoInicial} />
            </TransicaoSuave>
          </group>
        </group>
      )}
      {oficinaCorte?.ativa && posicaoFerramenta && demonstracaoOficina && !demonstracaoOficina.concluida && (
        <HaloZonaCorte3D
          posicao={posicaoFerramenta}
          executando={demonstracaoOficina.executando}
          recuando={demonstracaoOficina.recuando}
          pausado={demonstracaoOficina.pausado}
        />
      )}
      {oficinaTampa?.ativa && oficinaTampa.ferramentaVisivel && (
        oficinaTampa.ferramenta === 'alargador'
        || oficinaTampa.ferramenta === 'cortadorHidraulico'
        || oficinaTampa.ferramenta === 'extensor'
        || oficinaTampa.ferramenta === 'hooligan'
      ) && (
        posicaoFerramentaTampa && <FerramentaPosicionavel3D
          ferramenta={oficinaTampa.ferramenta}
          progresso={oficinaTampa.progresso}
          posicao={posicaoFerramentaTampa}
          anguloGraus={oficinaTampa.ajusteFerramenta.anguloGraus}
          lado="direito"
          plano="horizontal"
          giroPecaX={quadroTampaOficina?.giroX ?? 0}
          afastamento={oficinaTampa.afastamento}
          preparo={oficinaTampa.preparo}
          estadoInicial={oficinaTampa.estadoInicial}
          chaveTransicao={oficinaTampa.zonaSelecionada}
          deslizar={oficinaTampa.deslizar}
          desabilitarArraste={oficinaTampa.desabilitarArraste}
          aoMover={() => {}}
        />
      )}
      {oficinaTampa?.ativa && posicaoFerramentaTampa && demonstracaoOficina && !demonstracaoOficina.concluida && (
        <HaloZonaCorte3D
          posicao={posicaoFerramentaTampa}
          rotacao={[Math.PI / 2, 0, 0]}
          executando={demonstracaoOficina.executando}
          recuando={demonstracaoOficina.recuando}
          pausado={demonstracaoOficina.pausado}
        />
      )}
      {oficinaTerceiraPorta?.ativa && oficinaTerceiraPorta.ferramentaVisivel && oficinaTerceiraPorta.ferramenta === 'cortadorHidraulico' && (
        posicaoFerramentaTerceiraPorta && <FerramentaPosicionavel3D
          ferramenta={oficinaTerceiraPorta.ferramenta}
          progresso={oficinaTerceiraPorta.progresso}
          posicao={posicaoFerramentaTerceiraPorta}
          anguloGraus={oficinaTerceiraPorta.ajusteFerramenta.anguloGraus}
          lado={oficinaTerceiraPorta.lado}
          giroPeca={quadroTerceiraPortaOficina?.giroY ?? 0}
          afastamento={oficinaTerceiraPorta.afastamento}
          preparo={oficinaTerceiraPorta.preparo}
          estadoInicial={oficinaTerceiraPorta.estadoInicial}
          chaveTransicao={oficinaTerceiraPorta.zonaSelecionada}
          deslizar={oficinaTerceiraPorta.deslizar}
          desabilitarArraste={oficinaTerceiraPorta.desabilitarArraste}
          aoMover={() => {}}
        />
      )}
      {oficinaTerceiraPorta?.ativa && posicaoFerramentaTerceiraPorta && demonstracaoOficina && !demonstracaoOficina.concluida && (
        <HaloZonaCorte3D
          posicao={posicaoFerramentaTerceiraPorta}
          executando={demonstracaoOficina.executando}
          recuando={demonstracaoOficina.recuando}
          pausado={demonstracaoOficina.pausado}
        />
      )}
    </>
  )
  const cena =
    modo === 'cinematica' ? (
      <CenaCinematica {...cinematica} />
    ) : modo === 'estabilizacao' ? (
      <CenaEstabilizacao {...estabilizacao} />
    ) : modo === 'tabletop' ? (
      <CenaTabletop
        {...tabletop}
        aoAlterarManipulacao={(ativa) => {
          setTabletopManipulando(ativa)
          tabletop.aoAlterarManipulacao?.(ativa)
        }}
        aoCancelarManipulacao={() => {
          setTabletopManipulando(false)
          tabletop.aoCancelarManipulacao?.()
        }}
      />
    ) : modo === 'extracao' ? (
      <CenaExtracao {...extracao} />
    ) : modo === 'vidros' && vidros ? (
      <CenaManejoVidros {...vidros} />
    ) : (
      oficinaAtiva
        ? <group
            position={[0, postoOficina.alturaApoio, 0]}
            rotation={[postoOficina.inclinacaoLongitudinalRad, 0, postoOficina.rolagemRad]}
          >
            {conteudoMaquete}
          </group>
        : conteudoMaquete
    )

  return (
    <div className="visualizador-3d">
      <Canvas
        // três quartos dianteiro pelo lado do motorista: é onde ficam a vítima, as
        // portas de interesse e as rotas de retirada
        camera={{ position: [4.9, 3.4, 5.3], fov: 42 }}
        dpr={RECURSOS_QUALIDADE[qualidade.nivel].dpr}
        shadows="percentage"
        gl={CONFIGURACAO_GL_ESTUDIO}
        // clicar no vazio limpa a seleção e o hit
        onPointerMissed={() => {
          contextoComHit.aoRegistrarHit?.(null)
          if (modo === 'tabletop') tabletop.aoSelecionar(null)
          else if (modo === 'estabilizacao') estabilizacao.aoSelecionar(null)
          else contexto.selecionar(null)
        }}
      >
        <MonitorQualidade
          adaptativa={qualidade.adaptativa}
          aoDecair={qualidade.aoDecair}
          aoSubir={qualidade.aoSubir}
        />
        <ContadorFps qualidade={qualidade.nivel} />
        <PalcoEstudio
          perfil={perfil}
          qualidade={qualidade.nivel}
          ampla={modo === 'tabletop'}
          diorama={modo === 'tabletop' && tabletop.exibirAsfalto !== false}
        />

        <ProvedorPosProcessamento>
          <ProvedorQualidade nivel={qualidade.nivel}>
          <ProvedorMaquete valor={contextoComHit}>
            {cena}
            <DestaqueSelecaoAnatomia />
            <DebugHitVeicular3D
              hit={contextoComHit.ultimoHit ?? null}
              ativo={contextoComHit.depuracaoHit ?? false}
            />
          </ProvedorMaquete>
          </ProvedorQualidade>
          <PosProcessamento qualidade={qualidade.nivel} oclusaoAmbiente={modo === 'tabletop'} />
        </ProvedorPosProcessamento>

        <OrbitControls
          makeDefault
          enabled={
            (modo !== 'extracao' || extracao.ferramenta !== 'trajeto') &&
            !(modo === 'tabletop' && (tabletopManipulando || tabletop.escolhendoAlvo)) &&
            !(modo === 'tabletop' && tabletop.pincel?.ativo && tabletop.objetos.some(
              (objeto) => objeto.id === tabletop.idSelecionado && Boolean(objeto.veiculo),
            ))
          }
          target={alvoPadraoCamera}
          minDistance={DISTANCIA_MINIMA}
          maxDistance={modo === 'tabletop' ? 30 : DISTANCIA_MAXIMA}
          maxPolarAngle={
            modo === 'extracao' && extracao.configuracao !== 'sobreRodas'
              ? Math.PI - 0.08
              : Math.PI / 2 - 0.02
          }
          enableDamping
          dampingFactor={0.08}
          enableZoom
          zoomSpeed={0.9}
          zoomToCursor
        />
        <ControleCamera3D
          comando={comandoCamera}
          perfil={perfil}
          posto={modo === 'extracao' ? postoDoVeiculo(perfil, extracao.configuracao) : undefined}
          enquadramentoInterior={
            modo === 'extracao' ? enquadramentoInteriorExtracao(perfil, extracao.configuracao) : null
          }
          enquadramentoOficina={poseDaOficina}
          aoInteragirManual={oficinaAtiva ? aoInteragirCameraOficina : undefined}
        />
        <CameraDaExtracao
          ativa={modo === 'extracao'}
          configuracao={extracao.configuracao}
          perfil={perfil}
        />
      </Canvas>

    </div>
  )
}
